import Http from "../http";
import i18n from "../../i18n";

// SAM-257: creteProject and cloneProject now report failure as
// `{ success: false, data }` (project.controller.ts replies `{ success, data }`).
//
// SAM-115 answered that open question: the two unreferenced importers
// (layout/partials/modals/tagging_modal.js, pages/projects/project_view_old.js)
// were deleted, and with them the twelve get/create/deleteProject{Factories,
// Materials,Customer,Contacts} helpers they were the only callers of.
//
// `e.response` is undefined whenever the request never reached the server —
// a timeout, a dropped connection, CORS. Reading `.data` off it threw a
// TypeError out of the catch block, so the function rejected instead of
// returning, and callers that `await` it without a try/catch died mid-handler.
// Same failure-is-undetectable family as SAM-257; guarded throughout.

export const getProjectsList = async (params) => {
  const path = `/v2/projects`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return response.data;
  } catch (e) {
    return [];
  }
};

export const getAllDayList = async (params) => {
  const path = "/v2/settings";
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });

    if (response.data && response.data.allDay) {
      return response.data.allDay;
    }
    return [];
  } catch (e) {
    return [];
  }
};

export const getAllProjectList = async (params) => {
  const path = `/v2/projects/all`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return response.data;
  } catch (e) {
    return [];
  }
};

export const getCompanies = async (params) => {
  const path = `/v2/projects/companies`;
  try {
    const response = await Http.sharedInstance.withAuth().get(path, {
      params: params,
    });
    return response.data;
  } catch (e) {
    return [];
  }
};

export const creteProject = async (params) => {
  const path = `/v2/projects`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path, params);
    return response.data;
  } catch (err) {
    // SAM-257: was `return err.response` — the raw axios response, or
    // `undefined` when the request never reached the server, in which case the
    // create-project wizard's `response.success` threw a TypeError instead of
    // showing its error state. Mirror the success shape and keep the server's
    // message, which is what that wizard renders.
    return {
      success: false,
      data:
        err.response && err.response.data
          ? err.response.data
          : { message: null },
    };
  }
};

export const cloneProject = async (params) => {
  const path = `/v2/projects/${params.id}/clone`;
  try {
    const response = await Http.sharedInstance.withAuth().post(path);
    return response.data;
  } catch (e) {
    // SAM-257: was `return []` — truthy, so project_row_wrapper.js's
    // try/catch never fired and it always reported "cloned".
    // project.controller.ts replies `{ success, data }`.
    return { success: false, data: null };
  }
};

export const updateProject = async (params) => {
  const paramsForUpdate = Object.assign({}, params);
  if (paramsForUpdate.users) {
    const projectUsers = [];

    for (const user of paramsForUpdate.users) {
      if (typeof user !== "string") {
        if (user._id) {
          projectUsers.push(user._id);
        } else if (user.value) {
          projectUsers.push(user.value);
        }
      }
    }

    if (projectUsers.length > 0) {
      paramsForUpdate.users = projectUsers;
    }
  }
  const path = `/v2/projects/${params.id}`;
  try {
    const response = await Http.sharedInstance
      .getClient()
      .put(path, paramsForUpdate);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const getProject = async (params) => {
  const path = `/v2/projects/${params.id}`;
  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {};
  }
};

export const getCover = async (params) => {
  const path = `/v2/projects/${params.id}/cover`;
  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {};
  }
};

export const deleteProject = async (params) => {
  const path = `/v2/projects/${params.id}`;
  try {
    const response = await Http.sharedInstance.getClient().delete(path);
    return response.data;
  } catch (e) {
    throw e;
  }
};

export const feedsProjects = async (params) => {
  const path = `/v2/projects/${params.id}/feeds?page=${params.page}&page_size=${params.page_size}`;
  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: e.response ? e.response.data : null,
    };
  }
};

export const projectsDashboard = async () => {
  const path = `/v2/projects/dashboard`;
  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const updateSignleProjectField = async (params) => {
  return updateProjectiFields({
    id: params.id,
    fields: [params.field],
  });
};

/**
 * SAM-309 — save one project field and say so if it did not save.
 *
 * `PATCH /v2/projects/:id/fields` answers `{ success: <boolean> }`, and refuses
 * a field the user's role may not edit with an empty 403 that
 * `updateProjectiFields` turns into `{ success: false }`. Every one of the
 * field editors discarded that and dispatched `CurrentProject.updateProject`
 * regardless, so the new value painted, stayed painted, and was gone on
 * reload — with a per-role refusal looking exactly like a successful edit.
 *
 * Same shape as `saveSkill` in pages/skills/skill_editor.js: the check lives
 * in one place so eight callers cannot drift apart again.
 *
 * Returns true when the value is really stored, which is the caller's cue to
 * update Redux.
 */
export const saveProjectField = async ({ id, field }) => {
  const response = await updateSignleProjectField({ id, field });
  if (response && response.success) {
    return true;
  }
  alert(i18n.t("default:_OPERATION_FAILURE"));
  return false;
};

export const updateProjectiFields = async (params) => {
  const path = `/v2/projects/${params.id}/fields`;
  try {
    const response = await Http.sharedInstance
      .getClient()
      .patch(path, params.fields);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const getListSuggestions = async ({ group, isPrivate, project }) => {
  const path = `/v2/projects/fields/list/${group}?isPrivate=${
    isPrivate ? 1 : 0
  }&project=${project}`;
  try {
    const response = await Http.sharedInstance.getClient().get(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const createListSuggestion = async (params) => {
  const path = `/v2/projects/fields/list`;
  try {
    const response = await Http.sharedInstance.getClient().post(path, params);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const createListSuggestionBulk = async (params) => {
  const path = `/v2/projects/fields/list/bulk`;
  try {
    const response = await Http.sharedInstance.getClient().post(path, params);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};


export const deleteListSuggestion = async ({ id }) => {
  const path = `/v2/projects/fields/list/${id}`;
  try {
    const response = await Http.sharedInstance.getClient().delete(path);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const changeFieldVisibility = async ({ id, ...rest }) => {
  const path = `/v2/projects/${id}/fields/visibility`;
  try {
    const response = await Http.sharedInstance.getClient().post(path, rest);
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
};

export const getAllFieldsValues = async () => {
  const path = `/v2/projects/fields/values`;
  try {
    const response = await Http.sharedInstance.getClient().get(path, {});
    return response.data;
  } catch (e) {
    return {
      success: false,
      data: {},
    };
  }
}
